// The arithmetic of a sponsor band, for any taxonomy: what renders, how wide it comes out, and what
// a move means. Two bands are administered in CP — Sportya's own partners (Parteneri) and the
// sponsors of one event (the event's Sponsors tab) — with two taxonomies, two storage shapes and
// one public rendering component (ADR-0016). The ordering rules are the same, so they live once.
//
// None of this touches React or the API. A band is described to `createBandLayout` once — its
// tiers, which field of a row names the tier, and which rows are live — and every question the
// editor asks is then a function of the stored list and today's date.

// Measured off SportyaSponsorsStrip (@tudormocuta-mpg/components 0.3.1), the component every public
// band renders with: a plate is 120px wide, plates sit 12px apart, tiers 57px apart, and the content
// column is 1194px. Below 96px the plates stop shrinking and the row wraps instead.
export const PLATE_WIDTH = 120
export const PLATE_FLOOR = 96
export const PLATE_GAP = 12
export const TIER_GAP = 57
export const BAND_WIDTH = 1194

export const asRows = rows => (Array.isArray(rows) ? rows.filter(Boolean) : [])

/** Display order inside a tier: by `order`, then by name so two rows sharing an order sit still. */
export const byOrderThenName = (a, b) =>
  (a.order ?? 0) - (b.order ?? 0) || String(a.name ?? "").localeCompare(String(b.name ?? ""))

/**
 * A band's own layout functions, bound to its taxonomy.
 *
 * `tiers` is the fixed list of `{ key, label }` in display order; `tierField` names the row field
 * that holds the tier key (`tier` for a system partner, `category` for an event sponsor); `isLive`
 * says whether a row is on the public band right now — the default is that every row is.
 */
export const createBandLayout = ({ tiers, tierField, isLive = () => true }) => {
  const tierOf = row => row?.[tierField]
  const withTier = (row, key) => ({ ...row, [tierField]: key })
  const tierExists = key => tiers.some(tier => tier.key === key)
  const liveRows = (rows, now) => asRows(rows).filter(row => isLive(row, now))

  /**
   * How wide the band comes out at a given plate size. Empty tiers cost nothing: the public band
   * skips them entirely, so four tiers with one row each are narrower than two tiers with three.
   */
  const bandWidth = (rows, tile = PLATE_WIDTH, now = new Date()) => {
    const live = liveRows(rows, now)
    const occupied = tiers
      .map(({ key }) => live.filter(row => tierOf(row) === key))
      .filter(({ length }) => length > 0)

    const plates = occupied.reduce(
      (total, tier) => total + tier.length * tile + (tier.length - 1) * PLATE_GAP,
      0
    )
    return Math.round(plates + Math.max(0, occupied.length - 1) * TIER_GAP)
  }

  /**
   * The capacity warning. Three outcomes, because they mean three different things to whoever is
   * adding a logo: it fits as designed, it only fits by shrinking every plate, or the band wraps
   * onto a second row and stops looking like one band.
   */
  const bandCapacity = (rows, now = new Date()) => {
    const full = bandWidth(rows, PLATE_WIDTH, now)
    if (full <= BAND_WIDTH) {
      return {
        tone: "success",
        badge: "fits",
        tile: PLATE_WIDTH,
        width: full,
        text: `One row, plates at full size (${full}px of ${BAND_WIDTH}px).`,
      }
    }

    const floor = bandWidth(rows, PLATE_FLOOR, now)
    if (floor <= BAND_WIDTH) {
      return {
        tone: "warning",
        badge: "tight",
        tile: PLATE_FLOOR,
        width: floor,
        text:
          `One row, but every plate shrinks to fit (${floor}px of ${BAND_WIDTH}px at the ` +
          `${PLATE_FLOOR}px floor). One more logo splits the band.`,
      }
    }

    return {
      tone: "danger",
      badge: "two rows",
      tile: PLATE_FLOOR,
      width: floor,
      text: `Past the floor — the band renders on two rows (${floor}px needed, ${BAND_WIDTH}px available).`,
    }
  }

  /**
   * The band as the editor draws it: every tier, each one a drop target, holding the rows that are
   * live right now in display order. Unlike the public band, an empty tier is kept — a tier you
   * cannot see is a tier you cannot drop into.
   */
  const bandTiers = (rows, now = new Date()) => {
    const live = liveRows(rows, now)

    return tiers.map(({ key, label }) => ({
      key,
      label,
      rows: live.filter(row => tierOf(row) === key).sort(byOrderThenName),
    }))
  }

  /**
   * What a drop means.
   *
   * Given the whole stored list and one move — this row, into that tier, landing before that plate
   * (or last, when `beforeId` is null) — returns the list as it will read and the rows whose tier or
   * order changed. A band written one row at a time sends only those; a band written whole sends
   * the list and ignores them.
   *
   * Both tiers are renumbered contiguously from 1, rows that are not live included. Those are not
   * on the band and cannot be dragged, but they still carry an `order` that decides where they land
   * when they come back — leaving gaps there makes a return reappear in an arbitrary place.
   */
  const moveRow = (rows, { id, toTier, beforeId = null } = {}) => {
    const all = asRows(rows)
    const moved = all.find(row => row.id === id)

    if (!moved || !tierExists(toTier)) return { rows: all, writes: [] }

    const fromTier = tierOf(moved)
    const affected = fromTier === toTier ? [toTier] : [fromTier, toTier]

    const target = all
      .filter(row => tierOf(row) === toTier && row.id !== id)
      .sort(byOrderThenName)

    const at = beforeId ? target.findIndex(row => row.id === beforeId) : -1
    const placed = [...target]
    placed.splice(at === -1 ? placed.length : at, 0, withTier(moved, toTier))

    const renumbered = new Map()
    affected.forEach(tier => {
      const members =
        tier === toTier
          ? placed
          : all.filter(row => tierOf(row) === tier && row.id !== id).sort(byOrderThenName)

      members.forEach((row, index) => {
        renumbered.set(row.id, { ...withTier(row, tier), order: index + 1 })
      })
    })

    const next = all.map(row => renumbered.get(row.id) || row)
    const writes = next.filter(row => {
      const before = all.find(other => other.id === row.id)
      return tierOf(before) !== tierOf(row) || (before.order ?? 0) !== row.order
    })

    return { rows: next, writes }
  }

  /**
   * The keyboard path to the same move. Drag is the fast way; this is the way that works without a
   * mouse, and it is the only way to reach a row that is off the band and has nothing to drag.
   */
  const shiftRow = (rows, id, direction, now = new Date()) => {
    const all = asRows(rows)
    const moved = all.find(row => row.id === id)
    if (!moved) return { rows: all, writes: [] }

    const siblings = bandTiers(all, now).find(({ key }) => key === tierOf(moved))?.rows || []
    const at = siblings.findIndex(row => row.id === id)
    const to = at + (direction === "up" ? -1 : 1)
    if (at === -1 || to < 0 || to >= siblings.length) return { rows: all, writes: [] }

    // Moving down means landing after the neighbour, which is the same as landing before whatever
    // follows it — and last of all when nothing does.
    const beforeId = direction === "up" ? siblings[to].id : siblings[to + 1]?.id || null

    return moveRow(all, { id, toTier: tierOf(moved), beforeId })
  }

  /** The order a row takes when it joins a tier: last, behind everything already there. */
  const nextOrderInTier = (rows, tier) =>
    asRows(rows)
      .filter(row => tierOf(row) === tier)
      .reduce((highest, row) => Math.max(highest, row.order ?? 0), 0) + 1

  /**
   * The order a saved form should carry. The form has no order field, and a write is the whole
   * row — so without this an edit would send `order: 0` and jerk the row to the front of its
   * tier. An edit that stays in its tier keeps its place; anything arriving in a tier goes last.
   */
  const orderForForm = (rows, { id, tier } = {}) => {
    const stored = id ? asRows(rows).find(row => row.id === id) : null
    return stored && tierOf(stored) === tier ? stored.order ?? 0 : nextOrderInTier(rows, tier)
  }

  return {
    bandWidth,
    bandCapacity,
    bandTiers,
    moveRow,
    shiftRow,
    nextOrderInTier,
    orderForForm,
  }
}
